/**
 * Timeline clip preview — plays a tagged clip's exact range inside the
 * [CLIP:tag] segment it belongs to.
 *
 * One instance is mounted at a time (in the segment being previewed) so a
 * long timeline never loads dozens of YouTube players.
 *
 *  ┌ ClipStrip ─ tag · source · range · badges · [Preview] ┐   (every clip segment)
 *  └ TimelineClipPreview ─ framed player + range controls  ┘   (the open one)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import {
  FaPlay, FaPause, FaRedo, FaStepBackward, FaStepForward, FaTimes, FaVolumeMute, FaVolumeUp,
  FaCropAlt, FaExternalLinkAlt, FaExclamationTriangle, FaFilm, FaListOl,
} from 'react-icons/fa';
import { parseClipTimestamp } from './clipTimeUtils';
import { classifyVideoUrl, getVideoSourceLabel, isReactPlayerControllable, sharePlaybackUrl } from './videoPlayback';
import './timeline-preview.css';

const RATES = [0.5, 1, 1.5, 2];
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export const fmtShort = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
};

const fmtStamp = (t) => (t ? String(t).replace(/^00:/, '') : '—');

/** Range in seconds for a clip; null when start/end are missing or invalid. */
export function clipRangeSeconds(range) {
  const s = parseClipTimestamp(range?.start);
  const e = parseClipTimestamp(range?.end);
  if (s == null || e == null || e <= s) return null;
  return { start: s, end: e, length: e - s };
}

/* ── Strip shown on every clip segment ─────────────────────────── */
export function ClipStrip({
  tag, loc, range, open, playingAll, onPreview, onOpenInVideos,
}) {
  if (!loc) {
    return (
      <div className="tlp-strip tlp-strip--missing" role="status">
        <FaExclamationTriangle aria-hidden="true" />
        <span>
          No clip is tagged <strong>{tag || '…'}</strong> yet — tag one in the Videos tab.
        </span>
      </div>
    );
  }
  const r = clipRangeSeconds(range);
  const clip = loc.clip || {};
  const canPlay = isReactPlayerControllable(loc.url) && !!r;
  return (
    <div className={`tlp-strip ${open ? 'is-open' : ''}`}>
      <span className="tlp-strip__icon" aria-hidden="true"><FaFilm /></span>
      <span className="tlp-strip__main">
        <span className="tlp-strip__line">
          <strong>{tag}</strong>
          <span className="tlp-strip__src">{getVideoSourceLabel(loc.url)} · {loc.character || 'clip'} #{(loc.clipIndex ?? 0) + 1}</span>
        </span>
        <span className="tlp-strip__line tlp-strip__range">
          {fmtStamp(range?.start)} → {fmtStamp(range?.end)}
          {r ? <em>{r.length.toFixed(1)}s</em> : <em className="is-warn">range incomplete</em>}
          {clip.crop && <span className="tlp-badge">crop {clip.crop.width}×{clip.crop.height}</span>}
          {clip.mute && <span className="tlp-badge">muted</span>}
          {clip.effect && <span className="tlp-badge">{clip.effect}</span>}
        </span>
      </span>
      <span className="tlp-strip__actions">
        <button type="button" className="tlp-btn tlp-btn--ghost" onClick={onOpenInVideos} title="Edit this clip in the Videos tab">
          <FaExternalLinkAlt aria-hidden="true" /><span>Edit</span>
        </button>
        <button
          type="button"
          className={`tlp-btn ${open ? 'tlp-btn--active' : 'tlp-btn--primary'}`}
          onClick={onPreview}
          disabled={!canPlay}
          aria-expanded={open}
          title={canPlay ? 'Preview this clip here' : 'This source or range can’t be previewed inline'}
        >
          {open ? <FaTimes aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
          <span>{open ? (playingAll ? 'Stop' : 'Close') : 'Preview'}</span>
        </button>
      </span>
    </div>
  );
}

/* ── The player ────────────────────────────────────────────────── */
export default function TimelineClipPreview({
  loc,
  range,
  tag,
  autoPlay = true,
  playAll = false,
  hasPrev,
  hasNext,
  position,
  onPrev,
  onNext,
  onEnded,
  onClose,
  onTogglePlayAll,
  onOpenInVideos,
}) {
  const r = clipRangeSeconds(range);
  const clip = useMemo(() => loc?.clip || {}, [loc]);
  const url = useMemo(() => sharePlaybackUrl(loc?.url || ''), [loc]);
  const kind = classifyVideoUrl(url).kind;
  const playerRef = useRef(null);
  const rootRef = useRef(null);
  const frameRef = useRef(null);
  const trackRef = useRef(null);
  const endedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(r?.start ?? 0);
  const [loop, setLoop] = useState(false);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(!!clip.mute);
  const [showCrop, setShowCrop] = useState(true);
  const [frameW, setFrameW] = useState(0);
  const [scrub, setScrub] = useState(null);

  useEffect(() => { setMuted(!!clip.mute); }, [clip.mute]);

  // measure the frame (for crop framing)
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setFrameW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // bring the player into view when it opens
  useEffect(() => {
    rootRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const seek = useCallback((sec, keepPlaying = true) => {
    const p = playerRef.current;
    if (!p || !r) return;
    const target = clamp(sec, r.start, r.end - 0.05);
    p.seekTo(target, 'seconds', keepPlaying);
    setT(target);
    endedRef.current = false;
  }, [r]);

  const handleReady = () => {
    if (ready) return;
    setReady(true);
    if (r) playerRef.current?.seekTo(r.start, 'seconds', true);
    if (autoPlay) setPlaying(true);
  };

  const handleProgress = ({ playedSeconds }) => {
    if (!r || scrub != null) return;
    setT(playedSeconds);
    if (playedSeconds < r.start - 0.5) { seek(r.start); return; }
    if (playedSeconds >= r.end - 0.05 && !endedRef.current) {
      if (loop) { seek(r.start); return; }
      endedRef.current = true;
      setPlaying(false);
      onEnded?.();
    }
  };

  const togglePlay = useCallback(() => {
    if (!r) return;
    if (!playing && (endedRef.current || t >= r.end - 0.1)) seek(r.start);
    setPlaying((v) => !v);
  }, [playing, r, seek, t]);

  const restart = () => { seek(r?.start ?? 0); setPlaying(true); };
  const nudge = (d) => seek(t + d);

  // keyboard while the preview has focus
  const onKeyDown = (e) => {
    const tagName = e.target?.tagName;
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;
    if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(e.shiftKey ? -5 : -1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(e.shiftKey ? 5 : 1); }
    else if (e.key.toLowerCase() === 'l') { setLoop((v) => !v); }
    else if (e.key.toLowerCase() === 'm') { setMuted((v) => !v); }
    else if (e.key === 'Escape') { onClose?.(); }
  };

  // range-scoped scrubber
  const timeAt = (clientX) => {
    const b = trackRef.current.getBoundingClientRect();
    return r.start + clamp((clientX - b.left) / (b.width || 1), 0, 1) * r.length;
  };
  const scrubDown = (e) => {
    if (!r) return;
    e.preventDefault();
    try { trackRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const v = timeAt(e.clientX);
    setScrub(v);
    seek(v);
  };
  const scrubMove = (e) => { if (scrub == null) return; const v = timeAt(e.clientX); setScrub(v); seek(v); };
  const scrubUp = (e) => {
    if (scrub == null) return;
    try { trackRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setScrub(null);
  };

  // crop framing: show exactly the saved crop region, at the crop's aspect
  const crop = showCrop && clip.crop?.width > 0 ? clip.crop : null;
  const srcW = crop?.expectedInputResolution?.width || 1920;
  const srcH = crop?.expectedInputResolution?.height || 1080;
  const aspect = crop ? crop.width / crop.height : 16 / 9;
  let innerStyle = { position: 'absolute', inset: 0 };
  if (crop && frameW > 0) {
    const k = frameW / crop.width; // px per source px
    innerStyle = {
      position: 'absolute',
      left: -crop.x * k,
      top: -crop.y * k,
      width: srcW * k,
      height: srcH * k,
    };
  }
  const mirror = /mirror/.test(clip.effect || '');
  const now = scrub ?? t;
  const pct = r ? clamp(((now - r.start) / r.length) * 100, 0, 100) : 0;

  return (
    <div
      ref={rootRef}
      className="tlp"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      role="region"
      aria-label={`Preview of clip ${tag}`}
    >
      <div
        ref={frameRef}
        className={`tlp-frame ${crop ? 'is-cropped' : ''}`}
        style={{ aspectRatio: `${aspect}`, '--tlp-ar': aspect }}
      >
        {kind === 'youtube' || kind === 'file' ? (
          <div style={{ ...innerStyle, transform: mirror ? 'scaleX(-1)' : undefined }}>
            <ReactPlayer
              ref={playerRef}
              url={url}
              playing={playing}
              muted={muted}
              playbackRate={rate}
              controls={false}
              width="100%"
              height="100%"
              progressInterval={100}
              onReady={handleReady}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onProgress={handleProgress}
              config={{
                youtube: { playerVars: { controls: 0, disablekb: 1, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3, fs: 0, start: Math.floor(r?.start || 0) } },
                file: { attributes: { playsInline: true, preload: 'auto' } },
              }}
            />
          </div>
        ) : (
          <div className="tlp-frame__note">This source can’t play inline.</div>
        )}
        {/* shield: clicks toggle play; YouTube UI never gets input */}
        <button type="button" className="tlp-shield" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
          {!ready && <span className="tlp-spinner" aria-hidden="true" />}
          {ready && !playing && <span className="tlp-bigplay" aria-hidden="true"><FaPlay /></span>}
        </button>
        <span className="tlp-frame__tag">
          {tag}{crop ? ' · cropped' : ''}{mirror ? ' · mirrored' : ''}
        </span>
        {position && <span className="tlp-frame__pos">{position}</span>}
      </div>

      <div
        ref={trackRef}
        className={`tlp-track ${scrub != null ? 'is-scrubbing' : ''}`}
        onPointerDown={scrubDown}
        onPointerMove={scrubMove}
        onPointerUp={scrubUp}
        onPointerCancel={scrubUp}
        role="slider"
        aria-label="Position in clip"
        aria-valuemin={0}
        aria-valuemax={r ? Number(r.length.toFixed(1)) : 0}
        aria-valuenow={r ? Number((now - r.start).toFixed(1)) : 0}
        tabIndex={0}
      >
        <span className="tlp-track__fill" style={{ width: `${pct}%` }} />
        <span className="tlp-track__thumb" style={{ left: `${pct}%` }} />
      </div>

      <div className="tlp-controls">
        <div className="tlp-controls__group">
          <button type="button" className="tlp-icon tlp-icon--play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} title="Play / pause (Space)">
            {playing ? <FaPause /> : <FaPlay />}
          </button>
          <button type="button" className="tlp-icon" onClick={restart} aria-label="Restart clip" title="Restart"><FaRedo /></button>
          <button type="button" className="tlp-icon tlp-icon--text" onClick={() => nudge(-1)} title="Back 1s (←)">−1s</button>
          <button type="button" className="tlp-icon tlp-icon--text" onClick={() => nudge(1)} title="Forward 1s (→)">+1s</button>
          <span className="tlp-time">
            {r ? `${fmtShort(now - r.start)} / ${fmtShort(r.length)}` : '—'}
          </span>
        </div>
        <div className="tlp-controls__group tlp-controls__group--end">
          <button type="button" className={`tlp-chip ${loop ? 'is-on' : ''}`} onClick={() => setLoop((v) => !v)} aria-pressed={loop} title="Loop (L)">Loop</button>
          <button type="button" className="tlp-chip tlp-chip--mono" onClick={() => setRate(RATES[(RATES.indexOf(rate) + 1) % RATES.length])} title="Speed">{rate}×</button>
          <button type="button" className={`tlp-icon ${muted ? 'is-on' : ''}`} onClick={() => setMuted((v) => !v)} aria-label={muted ? 'Unmute' : 'Mute'} title={clip.mute ? 'Clip is muted in the final video (M)' : 'Mute (M)'}>
            {muted ? <FaVolumeMute /> : <FaVolumeUp />}
          </button>
          {clip.crop && (
            <button type="button" className={`tlp-icon ${showCrop ? 'is-on' : ''}`} onClick={() => setShowCrop((v) => !v)} aria-pressed={showCrop} title={showCrop ? 'Showing crop — click for full frame' : 'Show crop'}>
              <FaCropAlt />
            </button>
          )}
        </div>
      </div>

      <div className="tlp-nav">
        <button type="button" className="tlp-btn tlp-btn--ghost" onClick={onPrev} disabled={!hasPrev} title="Previous clip segment">
          <FaStepBackward aria-hidden="true" /><span>Prev clip</span>
        </button>
        <button type="button" className={`tlp-btn ${playAll ? 'tlp-btn--active' : 'tlp-btn--ghost'}`} onClick={onTogglePlayAll} aria-pressed={playAll} title="Play every clip segment in order">
          <FaListOl aria-hidden="true" /><span>{playAll ? 'Playing all' : 'Play all'}</span>
        </button>
        <button type="button" className="tlp-btn tlp-btn--ghost" onClick={onNext} disabled={!hasNext} title="Next clip segment">
          <span>Next clip</span><FaStepForward aria-hidden="true" />
        </button>
        <span className="tlp-nav__spacer" />
        <button type="button" className="tlp-btn tlp-btn--ghost" onClick={onOpenInVideos} title="Edit this clip in the Videos tab">
          <FaExternalLinkAlt aria-hidden="true" /><span>Edit in Videos</span>
        </button>
        <button type="button" className="tlp-btn tlp-btn--ghost" onClick={onClose} title="Close (Esc)">
          <FaTimes aria-hidden="true" /><span>Close</span>
        </button>
      </div>
    </div>
  );
}
